import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, LogIn, LogOut, Menu, ShieldCheck, User, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CONTACT_EMAIL, LOGO, PROJECTS, SITE_URL, SOCIALS, ext } from "@/lib/links";
import { SocialIcon } from "@/components/SocialIcon";
import { CookieBanner } from "@/components/CookieBanner";
import { analyticsAvailable, initAnalytics, openPreferences } from "@/lib/analytics";

const item = "inline-flex min-h-[44px] items-center rounded-lg px-3 text-sm font-semibold text-[var(--luma-muted)] transition-colors hover:bg-[var(--luma-soft-purple)] hover:text-[var(--luma-action)]";
const active = ({ isActive }) => `${item} ${isActive ? "!text-[var(--luma-action)]" : ""}`;

function Brand({ size = "header" }) {
  const img = size === "header" ? "h-[54px] w-[54px] lg:h-16 lg:w-16" : "h-[52px] w-[52px]";
  return (
    <>
      <span className={size === "footer" ? "inline-flex rounded-xl bg-white p-1" : "inline-flex"}>
        <img src={LOGO} alt="Logo originale Lumacofono" width="64" height="64" className={`${img} shrink-0 object-contain`} />
      </span>
      <span className="font-display text-[22px] font-extrabold tracking-[-0.045em] lg:text-[25px]">Lumacofono</span>
    </>
  );
}

function SocialMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button type="button" className={`${item} gap-1`} aria-expanded={open} onClick={() => setOpen(!open)} data-testid="nav-social-toggle">
        Podcast e social <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 w-48 rounded-xl border border-border bg-white p-1.5 shadow-lg">
          {SOCIALS.map((s) => <a key={s.id} href={s.url} {...ext} className={`${item} flex`} data-testid={`nav-social-${s.id}`}>{s.label}</a>)}
        </div>
      )}
    </div>
  );
}

function Account({ onClick }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex flex-wrap items-center gap-1">
      {user?.role === "admin" && <NavLink to="/admin" onClick={onClick} className={active} data-testid="nav-admin-link"><ShieldCheck size={17} className="mr-1" /> Fondatore</NavLink>}
      <NavLink to={user ? "/profilo" : "/login"} onClick={onClick} className={active} data-testid={user ? "nav-profilo-link" : "nav-login-button"}>
        {user ? <User size={17} className="mr-1" /> : <LogIn size={17} className="mr-1" />} {user ? "Profilo" : "Accedi"}
      </NavLink>
      {user && <button onClick={() => { logout(); onClick?.(); }} className={item} aria-label="Esci" data-testid="nav-logout-button"><LogOut size={17} /></button>}
    </div>
  );
}

function MobileMenu({ close }) {
  return (
    <nav id="mobile-menu" className="absolute inset-x-0 top-full z-50 border-b border-border bg-white px-4 pb-5 pt-2 shadow-lg xl:hidden" data-testid="nav-mobile-menu" aria-label="Menu">
      <div className="grid">
        <NavLink to="/" end className={active} data-testid="nav-mobile-home">Le teorie dal mondo</NavLink>
        <NavLink to="/misteri" className={active} data-testid="nav-mobile-archive">Tutti i misteri</NavLink>
        <NavLink to="/classifica" className={active} data-testid="nav-mobile-classifica">Classifica</NavLink>
        {PROJECTS.map((p) => <a key={p.id} href={p.url} {...(p.sameTab ? {} : ext)} className={item} data-testid={`nav-mobile-${p.id}`}>{p.label}</a>)}
      </div>
      <p className="luma-label mt-3 px-3">Podcast e social</p>
      <div className="flex flex-wrap">{SOCIALS.map((s) => <a key={s.id} href={s.url} {...ext} className={item}>{s.label}</a>)}</div>
      <div className="mt-2 border-t border-border pt-2"><Account onClick={close} /></div>
    </nav>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <header className="relative z-40 border-b border-[var(--luma-line)] bg-white">
      <div className="mx-auto flex min-h-[76px] max-w-[1160px] items-center justify-between gap-3 px-4 lg:min-h-[88px]">
        <Link to="/" data-testid="nav-logo" aria-label="Lumacofono, homepage" className="flex min-w-0 items-center gap-3 text-[var(--luma-ink)]"><Brand /></Link>
        <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Navigazione principale">
          <NavLink to="/" end className={active} data-testid="nav-home-link">Le teorie dal mondo</NavLink>
          <NavLink to="/misteri" className={active} data-testid="nav-archive-link">Tutti i misteri</NavLink>
          {PROJECTS.map((p) => <a key={p.id} href={p.url} {...(p.sameTab ? {} : ext)} className={item} data-testid={`nav-${p.id}`}>{p.label}</a>)}
          <SocialMenu />
        </nav>
        <div className="hidden xl:block"><Account /></div>
        <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--luma-line)] bg-white xl:hidden" onClick={() => setOpen(!open)}
          aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Chiudi il menu" : "Apri il menu"} data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && <MobileMenu close={() => setOpen(false)} />}
    </header>
  );
}

const fLink = "flex min-h-[44px] items-center text-sm text-[#E0E8F3] transition-colors hover:text-white sm:min-h-[36px]";
const fHead = "mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-[#B6C5D9]";

function Footer() {
  return (
    <footer className="mt-16 rounded-t-[26px] bg-[var(--luma-night)] text-white sm:rounded-t-[32px]" data-testid="site-footer">
      <div className="mx-auto grid max-w-[1160px] gap-8 px-5 pb-6 pt-10 sm:grid-cols-2 md:grid-cols-[1.2fr_1fr_1fr]">
        <Link to="/" className="flex items-center gap-3 self-start text-white sm:col-span-2 md:col-span-1" data-testid="footer-brand"><Brand size="footer" /></Link>
        <nav aria-label="Le esperienze Lumacofono">
          <h2 className={fHead}>Esplora</h2>
          <Link to="/" className={fLink} data-testid="footer-link-teorie">Le teorie dal mondo</Link>
          {PROJECTS.map((p) => <a key={p.id} href={p.url} {...(p.sameTab ? {} : ext)} className={fLink} data-testid={`footer-link-${p.id}`}>{p.label}</a>)}
        </nav>
        <nav aria-label="Podcast e social">
          <h2 className={fHead}>Podcast e social</h2>
          {SOCIALS.map((s) => (
            <a key={s.id} href={s.url} {...ext} className={fLink} aria-label={`${s.label} (si apre in una nuova scheda)`} data-testid={`footer-link-${s.id}`}><SocialIcon id={s.id} size={15} className="mr-2.5 text-[#B6C5D9]" />{s.label} <span aria-hidden="true" className="ml-1 text-[#B6C5D9]">↗</span></a>
          ))}
        </nav>
      </div>
      <div className="mx-auto max-w-[1160px] px-5 pb-6">
        <div className="flex flex-col gap-4 border-t border-white/15 pt-5 text-xs text-[#B6C5D9] md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Lumacofono · Progetto fan-made indipendente.<br />Nessuna affiliazione con Eiichiro Oda, Shueisha o Toei Animation.</p>
          <nav className="flex flex-wrap gap-x-5" aria-label="Note legali">
            <Link to="/privacy" className={`${fLink} !text-xs`} data-testid="footer-link-privacy">Privacy</Link>
            <Link to="/cookie" className={`${fLink} !text-xs`} data-testid="footer-link-cookie">Cookie</Link>
            {analyticsAvailable && <button type="button" onClick={openPreferences} className={`${fLink} !text-xs`} data-testid="footer-cookie-preferences">Preferenze cookie</button>}
            <a href={`mailto:${CONTACT_EMAIL}`} className={`${fLink} !text-xs`} data-testid="footer-link-contact">Contatti</a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function useCanonical() {
  const { pathname } = useLocation();
  useEffect(() => {
    const url = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", url);
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", url);
    initAnalytics();
  }, [pathname]);
}

export function Layout({ children }) {
  useCanonical();
  return (
    <div className="flex min-h-screen flex-col overflow-x-clip">
      <Header />
      <main className="mx-auto w-full max-w-[1160px] flex-1 px-4 py-6 sm:px-5 sm:py-8">{children}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
